import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { salesReportApi, type SalesMeasure, type SalesMonthRow } from "@/lib/sales-report";
import { GroupedBarChart } from "@/components/charts/GroupedBarChart";
import { DetailModal } from "@/components/detail-modal";
import { EmptyState } from "@/components/empty-state";
import { ErrorState } from "@/components/error-state";
import { PageHeader } from "@/components/page-header";
import { formatDate } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Download, TrendingUp, CalendarDays } from "lucide-react";

const MEASURES: { key: SalesMeasure; label: string }[] = [
  { key: "revenue", label: "Revenue" },
  { key: "count", label: "Invoice count" },
  { key: "aov", label: "Avg order value" },
];

function currentFy(): number {
  const now = new Date();
  return now.getMonth() + 1 >= 7 ? now.getFullYear() + 1 : now.getFullYear();
}

export function SalesByMonthPage() {
  const thisFy = currentFy();
  const fyOptions = [thisFy, thisFy - 1, thisFy - 2, thisFy - 3];
  const [fy, setFy] = useState(thisFy);
  const [measure, setMeasure] = useState<SalesMeasure>("revenue");
  const [drillMonth, setDrillMonth] = useState<SalesMonthRow | null>(null);

  const query = useQuery({
    queryKey: ["sales-by-month", fy, measure],
    queryFn: () => salesReportApi.monthly(fy, measure),
  });

  const drillQuery = useQuery({
    queryKey: ["sales-drill", fy, drillMonth?.monthNumber],
    queryFn: () => (drillMonth ? salesReportApi.drilldown(fy, drillMonth.monthNumber) : Promise.resolve(null)),
    enabled: drillMonth !== null,
  });

  const data = query.data;
  const fmt = useMemo(
    () => (n: number) => (measure === "count" ? String(n) : n.toLocaleString()),
    [measure],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title="Sales by Month"
      />

      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        <Tabs value={measure} onValueChange={(v) => setMeasure(v as SalesMeasure)} className="w-full sm:w-auto">
          <TabsList className="grid w-full sm:w-[400px] grid-cols-3 h-9">
            {MEASURES.map((m) => (
              <TabsTrigger key={m.key} value={m.key} className="text-xs">
                {m.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <CalendarDays className="h-4 w-4 text-muted-foreground hidden sm:block" />
          <Select value={String(fy)} onValueChange={(v) => setFy(Number(v))}>
            <SelectTrigger className="w-full sm:w-[160px] h-9 text-xs">
              <SelectValue placeholder="Financial Year" />
            </SelectTrigger>
            <SelectContent>
              {fyOptions.map((y) => (
                <SelectItem key={y} value={String(y)} className="text-xs">
                  {`FY ${y - 1}/${y}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {data ? (
            <Button variant="outline" size="sm" className="h-9 text-xs" asChild>
              <a href={salesReportApi.csvUrl(fy, measure)}>
                <Download className="mr-2 h-3 w-3" />
                Export CSV
              </a>
            </Button>
          ) : null}
        </div>
      </div>

      {query.isError ? (
        <ErrorState onRetry={() => { void query.refetch(); }} />
      ) : !data || data.months.every((m) => m.selected === 0 && m.previous === 0) ? (
        <EmptyState 
          headline="No invoices found"
          description={`There is no data available for FY ${fy - 1}/${fy}. Try selecting a different year.`} 
        />
      ) : (
        <div className="flex flex-col gap-4">
          <Card className="flex flex-col shadow-sm">
            <CardHeader className="px-4 py-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                <CardTitle className="text-base">Performance Trend</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="flex-1 min-h-[300px] px-4 pb-4 pt-0">
              <GroupedBarChart
                data={data.months.map((m) => ({ label: m.monthLabel, selected: m.selected, previous: m.previous }))}
                selectedName={data.fyLabel}
                previousName={data.previousFyLabel}
                valueFormatter={fmt}
                onBarClick={(d) => {
                  const row = data.months.find((m) => m.monthLabel === d.label);
                  if (row) setDrillMonth(row);
                }}
              />
            </CardContent>
          </Card>

          <Card className="shadow-sm overflow-hidden flex flex-col">
            <CardHeader className="px-4 py-3 border-b bg-muted/20">
              <CardTitle className="text-base">Monthly Breakdown</CardTitle>
            </CardHeader>
            <div className="overflow-auto flex-1">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/10 hover:bg-muted/10">
                    <TableHead className="w-[120px]">Month</TableHead>
                    <TableHead className="text-right">{data.fyLabel}</TableHead>
                    <TableHead className="text-right">{data.previousFyLabel}</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.months.map((m) => (
                    <TableRow 
                      key={m.monthNumber} 
                      className="cursor-pointer hover:bg-muted/40 transition-colors" 
                      onClick={() => setDrillMonth(m)}
                    >
                      <TableCell className="font-medium text-slate-700">{m.monthLabel}</TableCell>
                      <TableCell className="text-right">{fmt(m.selected)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{fmt(m.previous)}</TableCell>
                      <TableCell className="text-right">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          m.variancePct === null ? "bg-slate-100 text-slate-600" :
                          m.variancePct > 0 ? "bg-emerald-100 text-emerald-700" :
                          m.variancePct < 0 ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"
                        }`}>
                          {m.variancePct === null ? "—" : `${m.variancePct > 0 ? '+' : ''}${m.variancePct}%`}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-50 hover:bg-slate-50 font-semibold border-t-2">
                    <TableCell>Total Year</TableCell>
                    <TableCell className="text-right text-primary">{fmt(data.totals.selected)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{fmt(data.totals.previous)}</TableCell>
                    <TableCell className="text-right">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${
                        data.totals.variancePct === null ? "bg-slate-100 text-slate-700" :
                        data.totals.variancePct > 0 ? "bg-emerald-100 text-emerald-700" :
                        data.totals.variancePct < 0 ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-700"
                      }`}>
                        {data.totals.variancePct === null ? "—" : `${data.totals.variancePct > 0 ? '+' : ''}${data.totals.variancePct}%`}
                      </span>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>
      )}

      <DetailModal
        open={drillMonth !== null}
        onOpenChange={(open) => !open && setDrillMonth(null)}
        title={drillMonth ? `Invoices — ${drillMonth.monthLabel} ${data?.fyLabel ?? ""}` : "Invoices"}
      >
        {drillQuery.data ? (
          <div className="space-y-3">
            {drillQuery.data.items.map((r) => (
              <div key={r.invoiceId} className="flex justify-between items-center p-3 rounded-lg border bg-card hover:bg-muted/40 transition-colors">
                <div className="flex flex-col gap-1">
                  <Link className="text-primary font-medium hover:underline flex items-center gap-2" to={`/invoices/${r.invoiceId}`}>
                    {r.invoiceReferenceNo}
                  </Link>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <CalendarDays className="h-3 w-3" />
                    {r.invoiceDate ? formatDate(r.invoiceDate) : "—"}
                  </span>
                </div>
                <span className="font-semibold">{fmt(r.totalIncGst)}</span>
              </div>
            ))}
            {drillQuery.data.items.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
                No invoices found for this month.
              </div>
            ) : null}
          </div>
        ) : (
          <EmptyState headline="Loading invoices…" />
        )}
      </DetailModal>
    </div>
  );
}
